import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, Chip, CircularProgress,
  LinearProgress, Divider, IconButton, TextField, InputAdornment,
  Table, TableHead, TableRow, TableCell, TableBody, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import LagbasLogo from '../components/LagbasLogo';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';

// ── Project status meta ───────────────────────────────────────────────────────
const PROJ_STATUS_META = {
  offertfas: { label: 'Offertfas', color: '#E65100', bg: '#FFF3E0' },
  aktiv:     { label: 'Aktivt',    color: '#1B5E20', bg: '#E8F5E9' },
  pausad:    { label: 'Pausat',    color: '#4A148C', bg: '#F3E5F5' },
  avslutad:  { label: 'Avslutat',  color: '#37474F', bg: '#ECEFF1' },
};
const PROJ_STATUS_ORDER = ['aktiv', 'offertfas', 'pausad', 'avslutad'];

// ── Offer status meta ─────────────────────────────────────────────────────────
const OFFER_STATUS_META = {
  draft:    { label: 'Utkast',     color: '#546E7A', bg: '#ECEFF1' },
  sent:     { label: 'Skickad',    color: '#1565C0', bg: '#E3F2FD' },
  accepted: { label: 'Accepterad', color: '#1B5E20', bg: '#E8F5E9' },
  declined: { label: 'Avböjd',     color: '#B71C1C', bg: '#FFEBEE' },
  expired:  { label: 'Utgången',   color: '#37474F', bg: '#ECEFF1' },
};
const OFFER_STATUS_ORDER = ['sent', 'draft', 'accepted', 'declined', 'expired'];

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtSEK(val) {
  if (!val) return '–';
  return Number(val).toLocaleString('sv-SE') + ' kr';
}

function fmtMonth(ym) {
  if (!ym) return '–';
  const names = ['jan','feb','mar','apr','maj','jun','jul','aug','sep','okt','nov','dec'];
  const [y, m] = ym.split('-');
  return names[parseInt(m, 10) - 1] + ' ' + y.slice(2);
}

function progressColor(pct) {
  if (pct > 100) return '#d32f2f';
  if (pct >= 80)  return '#f9a825';
  return '#2e7d32';
}

function generateMonths(startMonth, endMonth) {
  if (!startMonth || !endMonth) return [];
  const result = [];
  let [y, m] = startMonth.split('-').map(Number);
  const [ey, em] = endMonth.split('-').map(Number);
  while (y < ey || (y === ey && m <= em)) {
    result.push(`${y}-${String(m).padStart(2, '0')}`);
    m++; if (m > 12) { m = 1; y++; }
  }
  return result;
}

function calcBudgetTotals(budget) {
  const resources   = budget.resources  || [];
  const activities  = budget.activities || [];
  const months      = generateMonths(budget.startMonth, budget.endMonth);
  let totalHrs = 0, totalVal = 0;
  activities.forEach(act => {
    const hrs = months.reduce((s, m) => s + (parseFloat(act.months?.[m]) || 0), 0);
    totalHrs += hrs;
    const res = resources.find(r => r.id === act.resourceId);
    totalVal += hrs * (parseFloat(res?.hourlyRate) || 0);
  });
  const persons = resources.filter(r => r.userId).length;
  const roles   = resources.filter(r => !r.userId).length;
  return { totalHrs, totalVal, persons, roles, resourceCount: resources.length };
}

function calcUtfallHrs(milltimeImports) {
  const map = {};
  for (const imp of milltimeImports || []) {
    for (const entry of imp.entries || []) {
      if (!entry.name) continue;
      map[entry.name] = (map[entry.name] || 0) + (entry.hours || 0);
    }
  }
  return Object.values(map).reduce((s, v) => s + v, 0);
}

// ── Resource summary chip ─────────────────────────────────────────────────────
function ResourceSummary({ persons, roles }) {
  if (!persons && !roles) return <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled' }}>–</Typography>;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
      {persons > 0 && (
        <Chip icon={<PersonIcon sx={{ fontSize: '0.7rem !important' }} />}
          label={persons}
          size="small"
          sx={{ fontSize: '0.68rem', height: 18, bgcolor: '#E3F2FD', color: '#1565C0',
            '& .MuiChip-icon': { color: '#1565C0', ml: '4px' } }} />
      )}
      {roles > 0 && (
        <Chip icon={<BadgeIcon sx={{ fontSize: '0.7rem !important' }} />}
          label={roles}
          size="small"
          sx={{ fontSize: '0.68rem', height: 18, bgcolor: '#F3E5F5', color: '#6A1B9A',
            '& .MuiChip-icon': { color: '#6A1B9A', ml: '4px' } }} />
      )}
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function BudgetN() {
  const navigate = useNavigate();
  const [loading, setLoading]       = useState(true);
  const [items, setItems]           = useState([]); // unified projects + offers
  const [search, setSearch]         = useState('');
  const [typeFilter, setTypeFilter] = useState('');   // '' | 'projekt' | 'offert'
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    (async () => {
      const [projSnap, offerSnap] = await Promise.all([
        getDocs(query(collection(db, 'projects'), orderBy('name'))),
        getDocs(query(collection(db, 'offers'),   orderBy('title'))),
      ]);

      const projects = projSnap.docs
        .map(d => ({ id: d.id, ...d.data(), itemType: 'projekt' }))
        .filter(p => (p.budgets || []).some(b => (b.resources?.length > 0) || (b.activities?.length > 0)));

      const offers = offerSnap.docs
        .map(d => ({ id: d.id, ...d.data(), itemType: 'offert' }))
        .filter(o => (o.budgets || []).some(b => (b.resources?.length > 0) || (b.activities?.length > 0)));

      setItems([...projects, ...offers]);
      setLoading(false);
    })();
  }, []);

  // ── Derived counts ─────────────────────────────────────────────────────────
  const projItems  = items.filter(i => i.itemType === 'projekt');
  const offerItems = items.filter(i => i.itemType === 'offert');

  // Status counts within current type scope
  const scopedItems = typeFilter ? items.filter(i => i.itemType === typeFilter) : items;
  const statusCounts = {};
  scopedItems.forEach(i => {
    const s = i.status || (i.itemType === 'projekt' ? 'offertfas' : 'draft');
    statusCounts[s] = (statusCounts[s] || 0) + 1;
  });

  // ── Status options for current type ───────────────────────────────────────
  const statusOptions = typeFilter === 'offert'
    ? OFFER_STATUS_ORDER.filter(s => statusCounts[s] > 0)
    : PROJ_STATUS_ORDER.filter(s => statusCounts[s] > 0);

  // ── Filtered + sorted list ─────────────────────────────────────────────────
  const filtered = items
    .filter(item => {
      if (typeFilter && item.itemType !== typeFilter) return false;
      if (statusFilter && item.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const name   = item.itemType === 'projekt' ? (item.name || '') : (item.title || '');
        const number = item.itemType === 'projekt' ? (item.projectNumber || '') : (item.offerNumber || '');
        return name.toLowerCase().includes(q) || number.toLowerCase().includes(q);
      }
      return true;
    })
    .sort((a, b) => {
      // First: projects before offers when "Alla"
      if (!typeFilter && a.itemType !== b.itemType)
        return a.itemType === 'projekt' ? -1 : 1;
      // Then: by status order within type
      if (a.itemType === 'projekt') {
        const ao = PROJ_STATUS_ORDER.indexOf(a.status || 'offertfas');
        const bo = PROJ_STATUS_ORDER.indexOf(b.status || 'offertfas');
        if (ao !== bo) return ao - bo;
        return (a.name || '').localeCompare(b.name || '', 'sv');
      } else {
        const ao = OFFER_STATUS_ORDER.indexOf(a.status || 'draft');
        const bo = OFFER_STATUS_ORDER.indexOf(b.status || 'draft');
        if (ao !== bo) return ao - bo;
        return (a.title || '').localeCompare(b.title || '', 'sv');
      }
    });

  function getStatusMeta(item) {
    if (item.itemType === 'offert') {
      return OFFER_STATUS_META[item.status || 'draft'] || OFFER_STATUS_META.draft;
    }
    return PROJ_STATUS_META[item.status || 'offertfas'] || PROJ_STATUS_META.offertfas;
  }

  function getDisplayName(item) {
    return item.itemType === 'projekt' ? (item.name || '(namnlöst uppdrag)') : (item.title || '(namnlös offert)');
  }

  function getNumber(item) {
    return item.itemType === 'projekt' ? item.projectNumber : item.offerNumber;
  }

  function getNavPath(item, budget) {
    return item.itemType === 'projekt'
      ? `/projects/${item.id}?tab=budget`
      : `/offers/${item.id}`;
  }

  const emptyMsg = search || statusFilter || typeFilter
    ? 'Inga budgetar matchar filtret.'
    : 'Inga budgetar ännu.';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* ── Top bar ── */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Budget<span style={{ color: '#2196F3' }}>N</span>
            </Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>Budgetöversikt</Typography>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>

        {/* ── Type toggle ── */}
        <Box sx={{ mb: 2.5 }}>
          <ToggleButtonGroup
            exclusive
            value={typeFilter}
            onChange={(_, v) => { setTypeFilter(v ?? ''); setStatusFilter(''); }}
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 2.5, py: 0.9, fontSize: '0.85rem' } }}
          >
            <ToggleButton value="">
              <AccountBalanceWalletIcon sx={{ fontSize: 16, mr: 0.8 }} />
              Alla ({items.length})
            </ToggleButton>
            <ToggleButton value="projekt">
              <BusinessCenterIcon sx={{ fontSize: 16, mr: 0.8 }} />
              Uppdragsbudgetar ({projItems.length})
            </ToggleButton>
            <ToggleButton value="offert">
              <DescriptionIcon sx={{ fontSize: 16, mr: 0.8 }} />
              Offertbudgetar ({offerItems.length})
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* ── Filters ── */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small"
            placeholder={typeFilter === 'offert' ? 'Sök offert…' : typeFilter === 'projekt' ? 'Sök uppdrag…' : 'Sök…'}
            value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ width: 240 }} />

          {/* Status filter chips — only shown when type is selected */}
          {typeFilter && statusOptions.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip
                label={`Alla (${scopedItems.length})`}
                size="small"
                onClick={() => setStatusFilter('')}
                sx={{
                  cursor: 'pointer',
                  bgcolor: !statusFilter ? '#1a1a1a' : 'transparent',
                  color: !statusFilter ? 'white' : 'text.secondary',
                  border: `1px solid ${!statusFilter ? '#1a1a1a' : '#e0e0e0'}`,
                  fontWeight: !statusFilter ? 700 : 400,
                }}
              />
              {statusOptions.map(s => {
                const meta = typeFilter === 'offert' ? OFFER_STATUS_META[s] : PROJ_STATUS_META[s];
                const count = statusCounts[s] || 0;
                const sel = statusFilter === s;
                return (
                  <Chip key={s}
                    label={`${meta.label} (${count})`}
                    size="small"
                    onClick={() => setStatusFilter(sel ? '' : s)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: sel ? meta.bg : 'transparent',
                      color: sel ? meta.color : 'text.secondary',
                      border: `1px solid ${sel ? meta.color : '#e0e0e0'}`,
                      fontWeight: sel ? 700 : 400,
                    }}
                  />
                );
              })}
            </Box>
          )}

          {/* Legend for resource types */}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip icon={<PersonIcon sx={{ fontSize: '0.7rem !important' }} />} label="Person"
                size="small" sx={{ fontSize: '0.68rem', height: 18, bgcolor: '#E3F2FD', color: '#1565C0',
                  '& .MuiChip-icon': { color: '#1565C0', ml: '4px' } }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip icon={<BadgeIcon sx={{ fontSize: '0.7rem !important' }} />} label="Roll"
                size="small" sx={{ fontSize: '0.68rem', height: 18, bgcolor: '#F3E5F5', color: '#6A1B9A',
                  '& .MuiChip-icon': { color: '#6A1B9A', ml: '4px' } }} />
            </Box>
          </Box>
        </Box>

        {/* ── Content ── */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <AccountBalanceWalletIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary">{emptyMsg}</Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filtered.map(item => {
              const budgets    = item.budgets || [];
              const meta       = getStatusMeta(item);
              const utfallHrs  = item.itemType === 'projekt' ? calcUtfallHrs(item.milltimeImports) : 0;
              const displayName = getDisplayName(item);
              const number     = getNumber(item);
              const isOffer    = item.itemType === 'offert';

              return (
                <Paper key={item.id} sx={{ overflow: 'hidden' }}>
                  {/* ── Item header ── */}
                  <Box sx={{
                    px: 2.5, py: 1.5,
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#fafafa' },
                  }} onClick={() => navigate(isOffer ? `/offers/${item.id}` : `/projects/${item.id}`)}>
                    {/* Icon / image */}
                    {!isOffer && item.imageUrl ? (
                      <Box component="img" src={item.imageUrl} alt=""
                        sx={{ width: 72, height: 48, objectFit: 'cover', borderRadius: 0.5, flexShrink: 0 }} />
                    ) : (
                      <Box sx={{ width: 72, height: 48, borderRadius: 0.5, flexShrink: 0,
                        bgcolor: isOffer ? '#EDE7F6' : '#f0f0f0',
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {isOffer
                          ? <DescriptionIcon sx={{ fontSize: 22, color: '#7B1FA2' }} />
                          : <BusinessCenterIcon sx={{ fontSize: 22, color: '#ccc' }} />}
                      </Box>
                    )}

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {/* Type badge — only in "Alla" view */}
                        {!typeFilter && (
                          <Chip
                            label={isOffer ? 'Offert' : 'Uppdrag'}
                            size="small"
                            icon={isOffer
                              ? <DescriptionIcon sx={{ fontSize: '0.65rem !important' }} />
                              : <BusinessCenterIcon sx={{ fontSize: '0.65rem !important' }} />}
                            sx={{ fontSize: '0.62rem', height: 18,
                              bgcolor: isOffer ? '#EDE7F6' : '#E8EAF6',
                              color: isOffer ? '#6A1B9A' : '#283593',
                              '& .MuiChip-icon': { color: 'inherit', ml: '4px' },
                            }}
                          />
                        )}
                        <Typography fontWeight={700} noWrap>{displayName}</Typography>
                        {number && (
                          <Chip label={number} size="small"
                            sx={{ fontSize: '0.65rem', height: 18, bgcolor: '#f0f0f0' }} />
                        )}
                        <Chip label={meta.label} size="small"
                          sx={{ fontSize: '0.65rem', height: 18, bgcolor: meta.bg, color: meta.color, fontWeight: 600 }} />
                      </Box>
                      {/* Client name for offers */}
                      {isOffer && item.companyName && (
                        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.2 }}>
                          {item.companyName}
                        </Typography>
                      )}
                    </Box>
                    <OpenInNewIcon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
                  </Box>

                  {/* ── Budget table ── */}
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#fafafa' }}>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Budget</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Period</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="right">Resurser</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="right">Budg. tim</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }} align="right">Budg. värde</TableCell>
                        {!isOffer && (
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', minWidth: 140 }}>Utfall</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {budgets.map((budget, bi) => {
                        const { totalHrs, totalVal, persons, roles } = calcBudgetTotals(budget);
                        const showUtfall = !isOffer && bi === budgets.length - 1 && utfallHrs > 0;
                        const pct = showUtfall && totalHrs > 0 ? (utfallHrs / totalHrs) * 100 : 0;
                        const color = progressColor(pct);
                        return (
                          <TableRow key={budget.id}
                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f5f9ff' } }}
                            onClick={() => navigate(getNavPath(item, budget))}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                                <Typography sx={{ fontSize: '0.8rem' }}>
                                  {budget.name || `Budget ${bi + 1}`}
                                </Typography>
                                {budget.type === 'offert' && (
                                  <Chip label="offertbudget" size="small"
                                    sx={{ fontSize: '0.6rem', height: 16, bgcolor: '#EDE7F6', color: '#6A1B9A' }} />
                                )}
                                {budget.type === 'uppdrag' && (
                                  <Chip label="uppdragsbudget" size="small"
                                    sx={{ fontSize: '0.6rem', height: 16, bgcolor: '#E8EAF6', color: '#283593' }} />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                {budget.startMonth && budget.endMonth
                                  ? `${fmtMonth(budget.startMonth)} – ${fmtMonth(budget.endMonth)}`
                                  : '–'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <ResourceSummary persons={persons} roles={roles} />
                            </TableCell>
                            <TableCell align="right">
                              <Typography sx={{ fontSize: '0.8rem', fontWeight: totalHrs > 0 ? 600 : 400 }}>
                                {totalHrs > 0 ? `${totalHrs.toFixed(0)} tim` : '–'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography sx={{ fontSize: '0.8rem',
                                color: totalVal > 0 ? '#1B5E20' : 'text.secondary',
                                fontWeight: totalVal > 0 ? 600 : 400 }}>
                                {fmtSEK(totalVal)}
                              </Typography>
                            </TableCell>
                            {!isOffer && (
                              <TableCell>
                                {showUtfall ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ flex: 1 }}>
                                      <LinearProgress variant="determinate" value={Math.min(pct, 100)} sx={{
                                        height: 6, borderRadius: 3, bgcolor: '#e0e0e0',
                                        '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 },
                                      }} />
                                    </Box>
                                    <Typography sx={{ fontSize: '0.7rem', color, fontWeight: 600, minWidth: 36 }}>
                                      {pct.toFixed(0)}%
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
                                    {bi === budgets.length - 1 ? 'Inget utfall' : ''}
                                  </Typography>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Paper>
              );
            })}
          </Box>
        )}
      </Container>
    </Box>
  );
}
